import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Clock, MapPin, Star, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBookingModal } from "../contexts/BookingModalContext";

export const CityTransfersPage: React.FC = () => {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();
  const cities = [
    {
      name: "Amsterdam",
      image: "/images/links/amsterdam.webp",
      distance: "18 km",
      duration: "25-35 minutes",
      price: "€45-65",
      description:
        "Experience seamless transportation between Schiphol Airport and Amsterdam, the vibrant capital of the Netherlands. Our professional taxi service ensures you reach Amsterdam's historic canals, world-class museums, and bustling city center in comfort and style.",
      highlights: [
        "Direct route to Amsterdam Central Station",
        "Access to Museum Quarter and Jordaan district",
        "24/7 service to Dam Square and Red Light District",
        "Corporate transfers to business districts",
      ],
      attractions: "Anne Frank House, Van Gogh Museum, Rijksmuseum, Vondelpark",
    },
    {
      name: "Rotterdam",
      image: "/images/links/Rotterdam.webp",
      distance: "58 km",
      duration: "45-60 minutes",
      price: "€85-110",
      description:
        "Travel efficiently from Schiphol to Rotterdam, Europe's largest port city known for its modern architecture and innovative skyline. Our reliable taxi service connects you to Rotterdam's dynamic business district, cultural venues, and maritime attractions.",
      highlights: [
        "Direct access to Rotterdam Central Station",
        "Business transfers to Port of Rotterdam",
        "Route to Erasmus Bridge and Cube Houses",
        "Service to Feijenoord and city center",
      ],
      attractions: "Erasmus Bridge, Cube Houses, Markthal, Euromast Tower",
    },
    {
      name: "Utrecht",
      image: "/images/links/Utrecht.webp",
      distance: "45 km",
      duration: "35-50 minutes",
      price: "€70-90",
      description:
        "Connect seamlessly from Schiphol Airport to Utrecht, the heart of the Netherlands with its medieval old town and prestigious university. Our professional drivers ensure comfortable transportation to Utrecht's historic center and modern business districts.",
      highlights: [
        "Direct route to Utrecht Central Station",
        "Access to Dom Tower and historic city center",
        "University campus and student areas",
        "Business district and conference venues",
      ],
      attractions:
        "Dom Tower, Utrecht University, Oudegracht canals, Railway Museum",
    },
    {
      name: "Den Haag",
      image: "/images/links/Den Haag.webp",
      distance: "42 km",
      duration: "35-45 minutes",
      price: "€65-85",
      description:
        "Experience premium transportation from Schiphol to The Hague, the international city of peace and justice. Our taxi service provides direct access to government buildings, international courts, and the beautiful Scheveningen beach resort.",
      highlights: [
        "Government district and Binnenhof access",
        "International Court of Justice transfers",
        "Scheveningen beach and pier connections",
        "Embassy and diplomatic quarter service",
      ],
      attractions:
        "Binnenhof, Peace Palace, Scheveningen Beach, Mauritshuis Museum",
    },
    {
      name: "Eindhoven",
      image: "/images/links/Eindhoven.webp",
      distance: "125 km",
      duration: "75-90 minutes",
      price: "€150-190",
      description:
        "Reliable long-distance transportation from Schiphol to Eindhoven, the technology and design capital of the Netherlands. Our comfortable vehicles ensure a smooth journey to this innovative city known for Philips, high-tech industry, and modern design.",
      highlights: [
        "High-tech campus and business parks",
        "Eindhoven Airport connections",
        "Design and technology districts",
        "University and research facilities",
      ],
      attractions:
        "Philips Museum, Van Abbemuseum, Strijp-S district, TU/e Campus",
    },
    {
      name: "Tilburg",
      image: "/images/links/Tilburg.webp",
      distance: "95 km",
      duration: "60-75 minutes",
      price: "€120-150",
      description:
        "Professional taxi service from Schiphol Airport to Tilburg, a vibrant student city in North Brabant. Our reliable transportation connects you to Tilburg University, the historic city center, and the surrounding Brabant region.",
      highlights: [
        "Tilburg University campus access",
        "Historic city center and shopping districts",
        "Business and industrial areas",
        "Regional connections to North Brabant",
      ],
      attractions:
        "Tilburg University, De Pont Museum, Spoorpark, Textile Museum",
    },
    {
      name: "Groningen",
      image: "/images/links/Groningen.webp",
      distance: "185 km",
      duration: "120-140 minutes",
      price: "€220-280",
      description:
        "Long-distance premium transportation from Schiphol to Groningen, the vibrant student city in the north of the Netherlands. Our professional service ensures comfortable travel to this historic university town and regional capital.",
      highlights: [
        "University of Groningen campus",
        "Historic Grote Markt and city center",
        "Northern Netherlands regional hub",
        "Cultural venues and student districts",
      ],
      attractions:
        "Groninger Museum, Martinitoren, University of Groningen, Noorderplantsoen",
    },
    {
      name: "Maastricht",
      image: "/images/links/Maastricht.webp",
      distance: "200 km",
      duration: "130-150 minutes",
      price: "€240-300",
      description:
        "Premium long-distance taxi service from Schiphol to Maastricht, the historic capital of Limburg province. Our comfortable vehicles provide reliable transportation to this charming city near the Belgian and German borders.",
      highlights: [
        "Historic city center and Vrijthof square",
        "Maastricht University connections",
        "Cross-border access to Belgium/Germany",
        "Cultural and shopping districts",
      ],
      attractions:
        "Vrijthof Square, Sint-Servaasbasiliek, Maastricht University, Bonnefanten Museum",
    },
    {
      name: "Breda",
      image: "/images/links/Breda.webp",
      distance: "85 km",
      duration: "55-70 minutes",
      price: "€110-140",
      description:
        "Efficient transportation from Schiphol Airport to Breda, a historic fortress city in North Brabant. Our professional taxi service connects you to Breda's medieval center, modern business districts, and excellent rail connections.",
      highlights: [
        "Historic city center and Grote Kerk",
        "Breda Castle and fortress walls",
        "Business and commercial districts",
        "Regional transport hub connections",
      ],
      attractions: "Breda Castle, Grote Kerk, Begijnhof, Valkenberg Park",
    },
    {
      name: "Zwolle",
      image: "/images/links/Zwolle.webp",
      distance: "110 km",
      duration: "70-85 minutes",
      price: "€135-170",
      description:
        "Reliable taxi service from Schiphol to Zwolle, the historic Hanseatic city in Overijssel province. Our comfortable transportation ensures smooth travel to this charming medieval city and important regional center.",
      highlights: [
        "Historic Hanseatic city center",
        "Medieval architecture and city walls",
        "Regional business and transport hub",
        "Cultural venues and museums",
      ],
      attractions:
        "Sassenpoort, Museum de Fundatie, Historic city center, Grote Kerk",
    },
  ];

  return (
    <div className="pt-20 bg-gray-900 min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-linear-to-r from-gray-900 to-gray-800 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              {t("1294", "City Transfer Services")}
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              {t(
                "1295",
                "Professional taxi services from Schiphol Airport to major Dutch cities. Fixed pricing, 24/7 availability, and comfortable Mercedes-Benz fleet."
              )}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-gray-300">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>{t("1296", "24/7 Service")}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Star className="h-5 w-5" />
                <span>{t("1297", "Fixed Pricing")}</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5" />
                <span>{t("1298", "All Major Cities")}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t("1299", "Taxi Services to Dutch Cities")}
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              {t(
                "1300",
                "Reliable transportation from Schiphol Airport to all major Dutch cities. Professional drivers, comfortable vehicles, and transparent pricing."
              )}
            </p>
          </motion.div>

          <div className="space-y-16">
            {cities.map((city, index) => (
              <motion.div
                key={city.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className={`flex flex-col ${
                  index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                } items-center gap-12 bg-gray-800/50 backdrop-blur-xs rounded-2xl shadow-lg overflow-hidden p-8 border border-gray-700`}
              >
                {/* Image */}
                <div className="lg:w-1/2">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                    className="relative overflow-hidden rounded-xl shadow-xl"
                  >
                    <img
                      src={city.image}
                      alt={`Taxi to ${city.name}`}
                      className="w-full h-64 lg:h-80 object-cover"
                    />
                    <div className="absolute inset-0 bg-linear-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <h3 className="text-2xl font-bold">{city.name}</h3>
                      <p className="text-primary-light">
                        {city.distance} • {city.duration}
                      </p>
                    </div>
                  </motion.div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2 space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-4">
                      Taxi Schiphol {city.name}
                    </h3>
                    <p className="text-gray-300 leading-relaxed mb-6">
                      {city.description}
                    </p>
                  </div>

                  {/* Service Details */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-primary/10 p-4 rounded-lg">
                      <div className="flex items-center space-x-2 text-primary mb-2">
                        <MapPin className="h-4 w-4" />
                        <span className="font-semibold">
                          {t("1301", "Distance")}
                        </span>
                      </div>
                      <p className="text-white">{city.distance}</p>
                    </div>
                    <div className="bg-warning/10 p-4 rounded-lg">
                      <div className="flex items-center space-x-2 text-warning mb-2">
                        <Clock className="h-4 w-4" />
                        <span className="font-semibold">
                          {t("1302", "Duration")}
                        </span>
                      </div>
                      <p className="text-white">{city.duration}</p>
                    </div>
                  </div>

                  {/* Highlights */}
                  <div className="mb-6">
                    <div className="font-semibold text-white mb-3">
                      {t("1303", "Service Highlights:")}
                    </div>
                    <ul className="space-y-2">
                      {city.highlights.map((highlight, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <ArrowRight className="h-4 w-4 text-primary mt-1 shrink-0" />
                          <span className="text-gray-300 text-sm">
                            {highlight}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Attractions */}
                  <div className="mb-6">
                    <div className="font-semibold text-white mb-2">
                      {t("1304", "Popular Attractions:")}
                    </div>
                    <p className="text-gray-300 text-sm">{city.attractions}</p>
                  </div>

                  {/* Price and CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-600">
                    <div>
                      <span className="text-sm text-gray-400">
                        {t("1305", "Starting from")}
                      </span>
                      <p className="text-2xl font-bold text-primary">
                        {city.price}
                      </p>
                    </div>
                    <button
                      onClick={openModal}
                      className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                    >
                      {t("1306", "Book Now")}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t("1307", "Why Choose Our City Transfer Service?")}
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              {t(
                "1308",
                "Professional transportation solutions connecting Schiphol Airport to all major Dutch cities"
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Clock,
                title: t("1309", "24/7 Availability"),
                description: t(
                  "1310",
                  "Round-the-clock service, 365 days a year for all your travel needs"
                ),
              },
              {
                icon: Star,
                title: t("1311", "Fixed Pricing"),
                description: t(
                  "1312",
                  "Transparent, upfront pricing with no hidden fees or surge charges"
                ),
              },
              {
                icon: MapPin,
                title: t("1313", "All Major Cities"),
                description: t(
                  "1314",
                  "Comprehensive coverage of all major Dutch cities and regions"
                ),
              },
              {
                icon: Phone,
                title: t("1315", "Professional Service"),
                description: t(
                  "1316",
                  "Experienced drivers with local knowledge and excellent customer service"
                ),
              },
            ].map((feature) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center"
              >
                <div className="bg-primary/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-300 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t("1317", "Ready to Book Your City Transfer?")}
            </h2>
            <p className="text-xl text-white mb-8">
              {t(
                "1318",
                "Contact us for instant booking or get a personalized quote for your journey"
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={openModal}
                className="bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                {t("1319", "Book Online")}
              </button>
              <a
                href="tel:+31639182624"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors flex items-center justify-center space-x-2"
              >
                <Phone className="h-5 w-5" />
                <span>{t("1320", "Call +31 6 39182624")}</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
